/**
 * What the phone activity map's cards SAY and where their one action GOES — the agent card (Figma 2048:122213),
 * the connector card (Figma 2048:125261), the folded card a tray's "+N" opens, the "Connect more" card the tools
 * tray's "+" opens, and which card is open. Pure, so the copy and the routes are pinned
 * without rendering anything (`test/home3-phone-map.test.ts`); `Home3PhoneMapCards` only draws.
 *
 * The words are the desktop canvas's own for the same cards (`MapHome.describe`): "View {name}" is
 * `agents.viewAgent`, the Maestro's role and description are the Agents screen's, Connect / Manage are the map's.
 * Mirrors iOS/Android `ActivityMapScreen.agentCard` / `connectorCard` / `foldedCard` / `connectMoreCard`.
 */
import { connectorsPath } from '../connectors-route';
import { continueOnChannelHref } from '../external-channel';
import { channelChipTarget, chipConnected, foldedRows, hiddenChips, type Cluster } from './phone-layout';

/** The one card (or the Brief modal) open over the map — at most one at a time: opening one replaces the other. */
export type PhoneMapCard =
  | { kind: 'brief' }
  | { kind: 'maestro' }
  | { kind: 'agent'; id: string }
  | { kind: 'chip'; cluster: Cluster; slug: string }
  /** A tray's "+N": the chips it folds, as a list. Trays never expand. */
  | { kind: 'folded'; cluster: Cluster }
  /** The tools tray's "+": add a tool or an MCP server. */
  | { kind: 'connectMore' }
  /** The headline's Live Updates chip: the Recent activity sheet. */
  | { kind: 'live' };

export interface PhoneMapCardContent {
  title: string;
  /** The agent's role; on a connector, only the Wix card's site name. */
  subtitle?: string;
  /** Absent draws no paragraph — never a placeholder. */
  description?: string;
  /** Under the description, as they are: the Wix site's address, or the account's other sites, one each. */
  lines?: string[];
  actionLabel: string;
  /** Where the action goes, as an app route (the caller adds the mount prefix). */
  href: string;
  /** A quieter link under the action that leaves the app: the Wix card's "View site". */
  link?: { label: string; url: string };
}

/** One of the account's Wix sites, best first, as `/api/site-info` (web) and native-data `account-sites` (phones) serve it. */
export interface PhoneMapSite {
  name: string;
  viewUrl: string | null;
}

type T = (key: string, params?: Record<string, string | number>) => string;

const present = (s: string | undefined | null) => (s && s.trim() ? s : undefined);

/** A team member's card: their name, role and description from the roster, and the way to their page. */
export function agentCardContent(
  agent: { id: string; name: string; title?: string; description?: string },
  t: T,
): PhoneMapCardContent {
  return {
    title: agent.name,
    subtitle: present(agent.title),
    description: present(agent.description),
    actionLabel: t('agents.viewAgent', { name: agent.name }),
    href: `/agents/${encodeURIComponent(agent.id)}`,
  };
}

/** The Maestro's card: its short name, the Agents screen's role and line for it, and its page. */
export function maestroCardContent(names: { short: string; name: string }, t: T): PhoneMapCardContent {
  return {
    title: names.short,
    subtitle: t('agents.maestro.conductor'),
    description: t('agents.subtitle', { maestroAgentName: names.name }),
    actionLabel: t('agents.viewAgent', { name: names.short }),
    href: '/agents/maestro',
  };
}

/**
 * Where a chip leads — the card's action goes exactly there. A tool is its own row on the Tools tab (an MCP
 * server has no row of its own, so the tab's top); a channel is its section of the Channels tab.
 */
export function chipHref(cluster: Cluster, slug: string): string {
  if (cluster === 'channels') return connectorsPath({ tab: 'channels', target: channelChipTarget(slug) });
  return connectorsPath({ tab: 'tools', target: slug.startsWith('mcp:') ? undefined : slug });
}

/**
 * A chip's card. A tool reads its own Connectors-page description (`toolDescription`, resolved by the caller from
 * the connectors read); a channel reads the Channels screen's line for its section where one exists (WhatsApp, the
 * apps & web). Email, SMS, the AI coders and MCP servers get no paragraph — nothing is invented.
 */
export function chipCardContent(
  cluster: Cluster,
  chip: { slug: string; label: string },
  opts: { connected: boolean; hasPhone: boolean; toolDescription?: string; productName: string; sites?: readonly PhoneMapSite[] },
  t: T,
): PhoneMapCardContent {
  const connected = chipConnected(cluster, chip.slug, opts.connected, opts.hasPhone);
  if (cluster === 'connectors' && chip.slug === 'wix') return { ...wixBase(chip, opts, connected, t), ...wixSiteContent(opts.sites ?? [], t) };
  const description =
    cluster === 'connectors'
      ? present(opts.toolDescription)
      : chip.slug === 'whatsapp'
      ? t('connectors.about.whatsapp.mobile', { productName: opts.productName, mention: `@${opts.productName.toLowerCase()}` })
      : chip.slug === 'web' || chip.slug === 'phone-app'
      ? t('connectors.about.symphony-apps', { productName: opts.productName })
      : undefined;
  // The desktop canvas's two extra presses (`MapHome`, `channelsIn`): a connected SMS card SENDS one (Symphony's number in the
  // SMS app), and a connected WhatsApp card adds "Chat on WhatsApp" under Manage.
  if (cluster === 'channels' && chip.slug === 'sms-in' && connected) {
    return { title: chip.label, description, actionLabel: t('home.map.sendSms'), href: continueOnChannelHref('SMS') ?? chipHref(cluster, chip.slug) };
  }
  const chat = cluster === 'channels' && chip.slug === 'whatsapp' && connected ? continueOnChannelHref('WHATSAPP') : null;
  return {
    title: chip.label,
    description,
    actionLabel: t(connected ? 'home.map.manage' : 'home.map.connect'),
    href: chipHref(cluster, chip.slug),
    ...(chat ? { link: { label: t('home.map.chatOnWhatsapp'), url: chat } } : {}),
  };
}

/** What a chip card's press reports as `action` (`activity-map-chip:<cluster>`): the desktop canvas's vocabulary. */
export function chipCardAction(cluster: Cluster, slug: string, connected: boolean): 'manage' | 'connect' | 'send-sms' {
  if (!connected) return 'connect';
  return cluster === 'channels' && slug === 'sms-in' ? 'send-sms' : 'manage';
}

/** Where the Wix card's Manage goes: the Connectors page (desktop's `manageHref` for Wix). */
export const WIX_MANAGE_ROUTE = '/settings/connectors';

function wixBase(chip: { label: string }, opts: { toolDescription?: string }, connected: boolean, t: T): PhoneMapCardContent {
  return {
    title: chip.label,
    description: present(opts.toolDescription),
    actionLabel: t(connected ? 'home.map.manage' : 'home.map.connect'),
    // The desktop canvas's Wix card manages from the Connectors page itself, not a Tools row.
    href: WIX_MANAGE_ROUTE,
  };
}

/**
 * The Wix card names the account's own site, since the Symphony metasite is siteless — the desktop canvas's rule
 * (`MapHome.describe`), 1:1: the first site's name under the title; under the description, the other sites'
 * names, one each, or (one site) its address without the scheme; and "View site", which opens the first site.
 * No sites (none, or the read failed) adds nothing.
 */
export function wixSiteContent(sites: readonly PhoneMapSite[], t: T): Pick<PhoneMapCardContent, 'subtitle' | 'lines' | 'link'> {
  const first = sites[0];
  if (!first) return {};
  const url = first.viewUrl || null;
  const lines = sites.length > 1 ? sites.slice(1).map((s) => s.name) : url ? [url.replace(/^https?:\/\//, '').replace(/\/$/, '')] : undefined;
  return {
    ...(present(first.name) ? { subtitle: first.name } : {}),
    ...(lines ? { lines } : {}),
    ...(url ? { link: { label: t('home.map.viewSite'), url } } : {}),
  };
}

/**
 * Where the folded card's "Manage" goes: the Connectors screen, on the tab that lists everything the tray folds —
 * Tools for the tools tray, Channels for the channels tray (the desktop canvas's folded card, 1:1).
 */
export function foldedManageRoute(cluster: Cluster): string {
  return connectorsPath({ tab: cluster === 'channels' ? 'channels' : 'tools' });
}

export interface PhoneMapFoldedContent<C> {
  /** "{count} more" — every chip the tray folds. */
  title: string;
  /** How many the tray folds — the "+N" plate's number. */
  count: number;
  /** The rows the card lists, in tray order (at most `mapCardListMaxRows`). */
  rows: C[];
  /** The last row's "{n} more" when the list itself is cut short; absent when every hidden chip has its row. */
  more?: string;
  actionLabel: string;
  href: string;
}

/**
 * A tray's folded card: the chips past the first `maxChips` (`hiddenChips`, the "+N"'s own tail), listed by
 * `foldedRows`, and
 * "Manage" into the Connectors tab that lists them all. Null when the tray folds nothing (any more).
 */
export function foldedCardContent<C>(cluster: Cluster, items: readonly C[], t: T): PhoneMapFoldedContent<C> | null {
  const hidden = hiddenChips(items);
  if (hidden.length === 0) return null;
  const { shown, more } = foldedRows(hidden.length);
  return {
    title: t('home.map.moreHidden', { count: hidden.length }),
    count: hidden.length,
    rows: hidden.slice(0, shown),
    ...(more > 0 ? { more: t('home.map.moreHidden', { count: more }) } : {}),
    actionLabel: t('home.map.manage'),
    href: foldedManageRoute(cluster),
  };
}

/**
 * Where the "Connect more" card's "Connect" goes: the Connectors screen on its Tools tab — the route the folded
 * tools card's Manage takes.
 */
export function connectMoreRoute(): string {
  return foldedManageRoute('connectors');
}

/**
 * The "Connect more" card the tools tray's "+" opens — the desktop canvas's connect-more card (`MapHome`, the
 * connectors' `action: 'connect'` node), 1:1: "Connect more", "Add a tool or an MCP server your agents can use.",
 * and "Connect" into the Tools tab. Its mark is the tray's "+" (`PlusPlate`).
 */
export function connectMoreCardContent(t: T): PhoneMapCardContent {
  return {
    title: t('home.map.connectMore'),
    description: t('home.map.connectMoreText'),
    actionLabel: t('home.map.connect'),
    href: connectMoreRoute(),
  };
}
